<template>
  <div>
    <label v-if="label" class="col-form-label">
      {{ label }}
    </label>

    <div class="form-check d-flex align-items-baseline">
      <div class="radio-input-wrapper">
        <input id="radio-national" ref="radioNational" v-model="locationKind" type="radio" class="form-check-input"
               :name="locationKindFieldName" value="national" @change="locationKindChanged">
      </div>
      <label class="form-check-label" for="radio-national">
        <div class="form-check-content">
          {{ nationalOptionLabel }}
        </div>
        <p v-if="nationalOptionHint" class="form-check-content text-muted mb-0">
          {{ nationalOptionHint }}
        </p>
      </label>
    </div>
    <div class="form-check d-flex align-items-baseline">
      <div class="radio-input-wrapper">
        <input id="radio-local" v-model="locationKind" type="radio" class="form-check-input"
               :name="locationKindFieldName" value="local" @change="locationKindChanged">
      </div>
      <label class="local-option form-check-label flex-grow-1" for="radio-local">
        <div class="form-check-content">
          <div v-if="showLocalOptionLabel">
            {{ localOptionLabel }}
          </div>
          <p v-if="localOptionHint" class="text-muted">
            {{ localOptionHint }}
          </p>
          <template v-if="showLocationField">
            <custom-gmap-autocomplete id="location-query-field"
                                      ref="gmapAutocomplete"
                                      :value="locationQuery"
                                      :select-first-on-enter="true"
                                      :class="gmapAutocompleteClasses"
                                      :options="autocompleteOptions"
                                      name="query"
                                      @blur="locationQueryBlurred"
                                      @focus="setLocal"
                                      @input="updateQuery"
                                      @place-changed="handleAutocompletePlaceChanged" />
            <validation-errors v-if="hasValidationError" :errors="validationErrorsForDisplay" />
          </template>
        </div>
      </label>
    </div>

    <hidden-fields :field-group-name="name" :location="location" />
  </div>
</template>

<script>
import CustomGmapAutocomplete from './custom-gmap-autocomplete'
import GeocodeLocation from '../../mixins/geocode-location'
import HiddenFields from './locations/hidden-fields'
import { inject } from 'vue'
import isPresent from '../../helpers/is-present'
import ValidationErrors from './validation-errors'

export default {
  components: { CustomGmapAutocomplete, HiddenFields, ValidationErrors },

  mixins: [ GeocodeLocation ],

  props: {
    value: {
      type: Object,
      required: false
    },
    validationErrors: {
      type: [Object, Array],
      required: false,
      default: () => ({})
    },
    label: {
      type: String,
      required: false
    },
    locationKindFieldName: {
      type: String,
      required: false,
      default: 'location_kind'
    },
    nationalOptionHint: {
      type: String,
      required: false
    },
    nationalOptionLabel: {
      type: String,
      required: false,
      default() { return inject('i18n').t('member.petitions.national_campaign') }
    },
    localOptionHint: {
      type: String,
      required: false
    },
    localOptionLabel: {
      type: String,
      required: false,
      default: null
    },
    name: {
      type: String,
      required: false,
      default: 'location'
    }
  },

  emits: ['changed', 'location-kind-changed'],

  data() {
    return {
      // Used for serialization of geocoding when there is one from input being blurred and one from clicking autocomplete option
      blurGeocodingPromise: null,

      // Used by the GeocodeLocation mixin, setting to true avoids including the venue field in the location object
      excludeVenueField: true,

      locationKind: this.value === null ? 'national' : 'local',
      location: Object.assign({}, this.value), // Clone object received as prop to avoid updating from this child component
      currentQuery: null,
      showLocalOptionLabel: this.localOptionLabel && this.localOptionLabel.length
    }
  },

  computed: {
    boundaryBox() {
      return this.agraConfiguration.locationSearchBias.boundaryBox
    },

    country() {
      return this.agraConfiguration.organisationCountry
    },

    gmapAutocompleteClasses() {
      let attr = 'form-control query-input'
      if (this.hasValidationError) {
        attr = `${attr} is-invalid`
      }
      if (this.showLocalOptionLabel) {
        attr = `${attr} mt-4`
      }
      return attr
    },

    hasValidationError() {
      return this.validationErrors &&
        ((Array.isArray(this.validationErrors) && this.validationErrors.length > 0) ||
         (this.validationErrors.query && this.validationErrors.query.length > 0))
    },

    locationQuery() {
      // If there is a location loaded already, for eg from inititalLocation use its query. Otherwise, use
      // whatever the user has typed into the field
      if (this.currentQuery) {
        return this.currentQuery
      } else if (this.location && this.location.query ) {
        return this.location.query
      } else {
        return ''
      }
    },

    showLocationField() {
      return (this.locationKind === 'local') || !this.showLocalOptionLabel
    },

    validationErrorsForDisplay() {
      if (Array.isArray(this.validationErrors)) {
        return this.validationErrors
      } else {
        return this.validationErrors.query
      }
    }
  },

  methods: {
    changeAttribute() {
      this.updateQuery(this.location.query, true)
      this.$emit('changed', this.location)
    },

    focus() {
      if (this.locationKind === 'national') {
        this.$refs.radioNational.focus()
      } else {
        this.$refs.gmapAutocomplete.focus()
      }
    },

    handleAutocompletePlaceChanged: async function(place) {
      if (this.blurGeocodingPromise) {
        await this.blurGeocodingPromise
        this.blurGeocodingPromise = null
      }
      this.autocompletePlaceChanged(place)
    },

    locationKindChanged() {
      if (this.locationKind === 'national') {
        this.resetLocation()
      }

      this.$emit('location-kind-changed', this.locationKind)
    },

    locationQueryBlurred() {
      // Automatically geocode the location if the user entered something on the query input but
      // didn't select an option from the autocomplete results
      if (isPresent(this.currentQuery) && (!this.location || !this.location.latitude)) {
        this.blurGeocodingPromise = new Promise((resolve) => {
          this.geocodeLocation(() => {
            if (this.geocodeSucceeded) {
              this.$emit('changed', this.location)
            }
            resolve(true)
          })
        })
      }

    },

    setLocal() {
      this.locationKind = 'local'
      this.$emit('location-kind-changed', this.locationKind)
    },

    setNational() {
      this.locationKind = 'national'
      this.currentQuery = null
      this.resetLocation()
      this.$emit('location-kind-changed', this.locationKind)
    },

    updateQuery(newQuery, skipLocationReset) {
      if (!skipLocationReset) {
        // Reset the location to ensure the location is geocoded again on blur.
        // We explicitly skip the reset when setting the query after the user
        // chose an option from the Autocomplete results
        this.resetLocation()
      }

      this.currentQuery = newQuery
    },

    resetLocation() {
      this.location.query = null
      this.location.street_number = null
      this.location.street = null
      this.location.postal_code = null
      this.location.country = null
      this.location.locality = null
      this.location.region = null
      this.location.latitude = null
      this.location.longitude = null

      this.$emit('changed', null)
    }
  }
}
</script>

<style lang="scss" scoped>
.form-check {
  margin-bottom: 1rem;
}
</style>
